import Autocomplete from "@mui/material/Autocomplete";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useGetEntityMemberPageQuery } from "_shared/entity-members/_api/entity-member-api";
import EntityMemberQuery, {
  ENTITY_MEMBER_QUERY_MAXIMUM_LENGTH,
} from "_shared/entity-members/_api/queries/entity-member-query";
import EntityMemberSummary from "_shared/entity-members/entity-member-summary";
import Guid from "_shared/guids/guid";
import Individual from "marketplace/individuals/individual";
import IndividualSummary from "marketplace/individuals/profiles/summaries/individual-summary";
import { useEffect, useMemo, useState } from "react";
import { useSession } from "users/__session/session-context";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { useSelector } from "react-redux";
import { getIndividualByUserId } from "marketplace/individuals/_stores/individuals-redux-slice";

const SEARCH_DEBOUNCE_MILLISECONDS = 300;
const MATCHES_PAGE = PaginationParameters.create({ pageIndex: 0, pageSize: 25 });

const Container = styled(FormControl)(() => ({
  maxWidth: '100%',
  minWidth: '33%'
}));

type CompanyProfileContactPersonInputProps = {
  className?: string;
  required?: boolean;
  contactUserId?: Guid;
  onChange: (value: ContactPersonOption | null) => void;
};

type ContactPersonOption = Individual | IndividualSummary;

export default function CompanyProfileContactPersonInput(
  props: Readonly<CompanyProfileContactPersonInputProps>) {
  const {
    className,
    required,
    contactUserId,
    onChange
  } = props;

  // #region Hooks
  const session = useSession();

  const legalEntityId = session.user!.legalEntityId;

  // The firm is searched on the server as the person types, one page of matches at a time, rather
  // than loaded whole and filtered here (#5442).
  const [inputText, setInputText] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    // Cut to what the query accepts: a longer search would throw while rendering, not just fail.
    const timer = setTimeout(
      () => setSearch(inputText.trim().slice(0, ENTITY_MEMBER_QUERY_MAXIMUM_LENGTH)),
      SEARCH_DEBOUNCE_MILLISECONDS,
    );
    return () => clearTimeout(timer);
  }, [inputText]);

  // #region Selectors
  const query = useMemo(
    () => new EntityMemberQuery({ paginationParameters: MATCHES_PAGE, query: search }),
    [search],
  );
  const { data: matchesPage, isFetching: isLoadingCoworkers } = useGetEntityMemberPageQuery({
    legalEntityId: legalEntityId.value,
    query,
  });
  // A contact is a marketplace individual (Company.ContactId), so a member without one is not offered.
  const coworkers = useMemo<ContactPersonOption[]>(
    () =>
      EntityMemberSummary.fromJSONArray(matchesPage?.data ?? [])
        .map((member) => member.asIndividual())
        .filter((individual): individual is Individual => individual !== undefined),
    [matchesPage],
  );
  const contactUserIds = useMemo(() => new Set(contactUserId ? [contactUserId.value] : []), [contactUserId]);
  const { isLoading: isLoadingContactPerson } = useGetIndividualsByUserIdsQuery(
    { userIds: [...contactUserIds] },
    { skip: !contactUserId },
  );
  const contactPersonByUserId = useSelector(getIndividualByUserId(contactUserId));

  const options = useMemo<ContactPersonOption[]>(() => {
    const coworkerOptions = coworkers;
    if (!contactPersonByUserId) return coworkerOptions;
    const contactIsAlreadyIncluded = coworkerOptions.some((coworker) =>
      coworker.userId.isEqualTo(contactPersonByUserId.userId),
    );
    return contactIsAlreadyIncluded ? coworkerOptions : [contactPersonByUserId, ...coworkerOptions];
  }, [contactPersonByUserId, coworkers]);

  // Derive contactPerson directly from coworkers and contactUserId
  const contactPerson = useMemo(() => {
    if (!contactUserId) return null;
    return options.find((contactOption) => contactOption.userId.isEqualTo(contactUserId)) ?? null;
  }, [contactUserId, options]);
  // #endregion

  // #region Render
  return (
    <Container className={className}>
      <Autocomplete
        disablePortal
        getOptionLabel={getOptionLabel}
        getOptionKey={(option) => option.id.value}
        onChange={(_event, value) => {
          onChange(value);
        }}
        onInputChange={(_event, text, reason) => {
          // Only typing is a search. A choice, a reset or a blur writes the chosen name into the
          // box, which is not a search for that name, so the next look at the list is unfiltered.
          setInputText(reason === "input" ? text : "");
        }}
        value={contactPerson}
        options={options}
        isOptionEqualToValue={(option: unknown, value: unknown) => {
          if (!option || !value) return false;
          const coworker = option as ContactPersonOption;
          const selectedCoworker = value as ContactPersonOption;
          return coworker.id.isEqualTo(selectedCoworker.id);
        }}
        loading={isLoadingCoworkers || isLoadingContactPerson}
        // The server already matched the text, on the email address as well as the name.
        filterOptions={(contactOptions) => contactOptions}
        noOptionsText="No matching coworkers found"
        renderOption={(liProps, coworker) => {
          const { key, children: _children, ...optionProps } = liProps;
          void _children;

          return (
            <li key={key} {...optionProps}>
              <ListItemAvatar>
                <Avatar>
                  {`${coworker.firstName.slice(0, 1)}${coworker.lastName.slice(0, 1)}`}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={`${coworker.firstName} ${coworker.lastName}`}
                secondary={getContactEmail(coworker)?.toString() ?? ""}
              />
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            margin="none"
            variant="outlined"
            required={required}
            label="Contact Person"
            slotProps={{
              ...params.slotProps,
              htmlInput: { ...params.slotProps.htmlInput, maxLength: ENTITY_MEMBER_QUERY_MAXIMUM_LENGTH },
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {isLoadingCoworkers || isLoadingContactPerson
                      ? <CircularProgress color="inherit" size={20} disableShrink />
                      : null
                    }
                    {params.slotProps.input.endAdornment}
                  </>
                )
              }
            }}
          />
        )}
      />
    </Container>
  );
}

function getOptionLabel(option: unknown) {
  if (!option) return 'None';
  const coworker = option as ContactPersonOption;
  return `${coworker.firstName} ${coworker.lastName}`;
}

function getContactEmail(contactOption: ContactPersonOption) {
  return contactOption instanceof Individual ? contactOption.email : contactOption.emailAddress;
}
